import * as XLSX from 'xlsx';

interface ExcelStyle {
  headerFill: string;
  headerFont: string;
  alternateRowFill: string;
}

const defaultStyle: ExcelStyle = {
  headerFill: 'FFB4C7E7', // Light blue
  headerFont: 'FFFFFFFF', // White
  alternateRowFill: 'FFF2F2F2', // Light gray
};

/**
 * Export data to Excel with professional formatting
 */
export const exportToExcel = (
  data: any[],
  sheetName: string,
  filename: string,
  columns?: string[]
) => {
  if (!data || data.length === 0) {
    alert('No data to export');
    return;
  }

  // Create workbook
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);

  // Apply styling
  applyExcelFormatting(ws, data, columns);

  // Write file
  XLSX.writeFile(wb, `${filename}.xlsx`);
};

/**
 * Export multiple sheets to single Excel file
 */
export const exportMultipleSheets = (
  sheetsData: Array<{
    data: any[];
    sheetName: string;
    columns?: string[];
  }>,
  filename: string
) => {
  const wb = XLSX.utils.book_new();

  sheetsData.forEach(({ data, sheetName, columns }) => {
    if (data && data.length > 0) {
      const ws = XLSX.utils.json_to_sheet(data);
      applyExcelFormatting(ws, data, columns);
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    }
  });

  XLSX.writeFile(wb, `${filename}.xlsx`);
};

/**
 * Apply professional formatting to worksheet
 */
const applyExcelFormatting = (
  ws: XLSX.WorkSheet,
  data: any[],
  columns?: string[]
) => {
  if (!ws['!cols']) {
    ws['!cols'] = [];
  }

  // Set column widths
  const maxColumns = columns?.length || Object.keys(data[0] || {}).length;
  for (let i = 0; i < maxColumns; i++) {
    ws['!cols']![i] = { wch: 18 };
  }

  // Style header row
  const headerRowKeys = columns || Object.keys(data[0] || {});
  headerRowKeys.forEach((key, index) => {
    const cellAddress = XLSX.utils.encode_col(index) + '1';
    if (ws[cellAddress]) {
      ws[cellAddress].fill = { patternType: 'solid', fgColor: { rgb: defaultStyle.headerFill } };
      ws[cellAddress].font = { bold: true, color: { rgb: defaultStyle.headerFont } };
      ws[cellAddress].alignment = { horizontal: 'center', vertical: 'center', wrapText: true };
    }
  });

  // Style data rows with alternating colors
  data.forEach((row, rowIndex) => {
    const excelRowIndex = rowIndex + 2; // +1 for header, +1 because Excel is 1-indexed
    headerRowKeys.forEach((key, colIndex) => {
      const cellAddress = XLSX.utils.encode_col(colIndex) + excelRowIndex;
      if (ws[cellAddress]) {
        // Alternate row colors
        if (rowIndex % 2 === 0) {
          ws[cellAddress].fill = { patternType: 'solid', fgColor: { rgb: defaultStyle.alternateRowFill } };
        }
        
        // Center align numbers and dates
        const value = row[key];
        if (typeof value === 'number' || value instanceof Date) {
          ws[cellAddress].alignment = { horizontal: 'center', vertical: 'center' };
        } else {
          ws[cellAddress].alignment = { horizontal: 'left', vertical: 'center' };
        }

        // Format numbers with thousand separators
        if (typeof value === 'number' && !Number.isInteger(value)) {
          ws[cellAddress].numFmt = '0.00';
        } else if (typeof value === 'number') {
          ws[cellAddress].numFmt = '#,##0';
        }
      }
    });
  });
};

/**
 * Format data for Breakdown tab export
 */
export const formatBreakdownData = (data: any[]) => {
  return data.map((item) => ({
    'Downtime Reason': item.downtime_reason,
    'Category': item.category,
    'Occurrences': item.total_occurrences,
    'Total Duration (HH:MM:SS)': item.total_duration || '00:00:00',
    'Average Duration (HH:MM:SS)': item.avg_duration || '00:00:00',
  }));
};

/**
 * Format data for Print Orders tab export
 */
export const formatPrintOrdersData = (data: any[], isMachineData: boolean = false) => {
  if (isMachineData) {
    // Format machine summary data
    return data.map((item) => ({
      'Machine': item.machine_name,
      'No. of Copies Printed': item.total_pos,
      'Total Pages': item.total_pages || 0,
      'Avg Pages/Copy': Math.round(item.avg_pages_per_record || 0),
      'Min Pages': item.min_pages,
      'Max Pages': item.max_pages,
      'Publications': item.publications_printed,
      'Newsprint (Kg)': Math.round(item.total_newsprint_kgs * 100) / 100,
      'Total Plates': item.total_plates,
    }));
  }
  
  // Format publication summary data with ALP calculation
  return data.map((item) => ({
    'Publication': item.publication_name || 'Unknown',
    'No. of Copies Printed': item.total_pos,
    'Total Pages': item.total_pages || 0,
    'Newsprint Waste (Qty)': item.total_newsprint_waste || 0,
    'ALP': Math.round(item.total_alp || 0),
  }));
};

/**
 * Format data for Machine tab export
 */
export const formatMachineData = (data: any[]) => {
  return data.map((item) => ({
    'Machine Name': item.machine_name,
    'Total POs': item.total_pos || 0,
    'Total Records': item.total_records || 0,
    'Total Pages': item.total_pages || 0,
    'Avg Pages Per Record': item.avg_pages_per_record || 0,
    'Min Pages': item.min_pages || 0,
    'Max Pages': item.max_pages || 0,
    'Publications': item.publications_printed || 0,
    'Newsprint (kg)': item.total_newsprint_kgs || 0,
    'Plates (units)': item.total_plates || 0,
  }));
};

/**
 * Format data for LPRS tab export
 */
export const formatLPRSData = (data: any[]) => {
  return data.map((item) => ({
    'Date': item.date,
    'Total LPRS Time': item.total_lprs_time || '00:00:00',
    'Average LPRS Time': item.avg_lprs_time || '00:00:00',
    'Total Records': item.total_records || 0,
  }));
};

/**
 * Format data for Newsprint tab export
 */
export const formatNewsprintData = (data: any[]) => {
  return data.map((item) => ({
    'Date': item.date,
    'Total Newsprint (kg)': item.total_newsprint_kgs || 0,
    'Average Newsprint (kg)': item.avg_newsprint_kgs || 0,
    'Total Records': item.total_records || 0,
  }));
};

/**
 * Format data for Plate Consumption tab export
 */
export const formatPlateConsumptionData = (data: any[]) => {
  return data.map((item) => ({
    'Date': item.date,
    'Total Plates (units)': item.total_plates || 0,
    'Average Plates (units)': item.avg_plates || 0,
    'Total Records': item.total_records || 0,
  }));
};

/**
 * Format data for Duration tab export
 */
export const formatDurationData = (data: any[]) => {
  return data.map((item) => ({
    'Date': item.date,
    'Total Duration': item.total_duration || '00:00:00',
    'Average Duration': item.avg_duration || '00:00:00',
    'Total Records': item.total_records || 0,
    'Hours Per Page': item.hours_per_page || 0,
  }));
};

/**
 * Format data for Towers tab export
 */
export const formatTowersData = (data: any[]) => {
  return data.map((item) => ({
    'Date': item.date || item.record_date,
    'Towers': item.tower_ids ? `Tower ${item.tower_ids.join(', Tower ')}` : 'No Tower',
    'No. of Copies Printed': item.po_number || '-',
    'Publication': item.publication_name || '-',
    'Machine': item.machine_name || '-',
    'Pages': item.total_pages || 0,
    'User': item.user_name || '-',
    'Location': item.user_location || item.location || '-',
  }));
};

/**
 * Format data for Wastes tab export
 */
export const formatWastesData = (
  newsprintData: any[],
  platesData: any[],
  monthlyNewsprintData: any[] = [],
  monthlyPlatesData: any[] = [],
  monthlyWastePercentData: any[] = []
) => {
  return {
    newsprint: newsprintData.map((item) => ({
      'Date': item.date || '-',
      'Location': item.location,
      'Machine': item.machine_name,
      'Pages/Sheets': item.total_quantity || item.pages_sheets || 0,
      'Newsprint (kg)': item.total_weight || item.newsprint_kgs || 0,
    })),
    plates: platesData.map((item) => ({
      'Date': item.date,
      'Location': item.location,
      'Machine': item.machine_name,
      'Plates (units)': item.total_quantity || 0,
      'Category': item.category_type || '-',
    })),
    monthlyNewsprint: monthlyNewsprintData.map((item) => ({
      'Month': item.month_label || item.month || '-',
      'Location': item.location || '-',
      'Machine': item.machine_name || '-',
      'Pages/Sheets': item.total_quantity || 0,
      'Newsprint (kg)': item.total_weight || 0,
      'Records': item.total_records || 0,
    })),
    monthlyPlates: monthlyPlatesData.map((item) => ({
      'Month': item.month_label || item.month || '-',
      'Location': item.location || '-',
      'Machine': item.machine_name || '-',
      'Plates (units)': item.total_quantity || 0,
      'Records': item.total_records || 0,
    })),
    monthlyWastePercent: monthlyWastePercentData.map((item) => ({
      'Month': item.month_label || item.month || '-',
      'Location': item.location || '-',
      'Machine': item.machine_name || '-',
      'Waste %': item.waste_percentage || 0,
      'Waste (kg)': item.total_waste_kgs || 0,
      'Newsprint (kg)': item.total_newsprint_kgs || 0,
      'Records': item.total_records || 0,
    })),
  };
};

/**
 * Format summary statistics for export
 */
export const formatStatistics = (stats: any, tabName: string) => {
  const baseStats: any = {
    'Tab': tabName,
    'Generated': new Date().toLocaleString(),
  };

  // Add all statistics fields
  Object.keys(stats).forEach((key) => {
    baseStats[key] = stats[key];
  });

  return [baseStats];
};
